import * as MatchStatus from 'customer-data-filters-ui/filterQueryFormat/MatchStatus';
import I18n from 'I18n';
import { useViewerActions } from '../../hooks/useViewerActions';
import ViewerConditionClickableBox from '../viewer/ViewerConditionClickableBox';
import ViewerCondition from './ViewerCondition';
import { useCallback, useContext } from 'react';
import ViewerRelatedConditionsHeading from './ViewerRelatedConditionsHeading';
import ViewerAddFilterButton from './ViewerAddFilterButton';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import ViewerConditionPlaceholder from './ViewerConditionPlaceholder';
import { isLogicGroup, LogicGroupNames } from '../../filterQueryFormat/logic/LogicGroup';
import RefHighlightContainer from '../common/RefHighlightContainer';
import getConditionId from '../../utils/getConditionId';
import { isPropertyNameAndValueCondition } from '../../strategy/propertyChangedFilterUtils';
import { useStrategy } from '../../hooks/useStrategy';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import styled from 'foundations-theming/utils';
import { isAnyEventTypeId } from '../../filterQueryFormat/ObjectTypeId';
import ViewerTestFiltersMembershipMatchDisplay from './ViewerTestFiltersMembershipMatchDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LOGIC_OPERATOR_GAP = '100';
const StyledConditionContentBox = styled.div.withConfig({
  displayName: "ViewerRelatedConditions__StyledConditionContentBox",
  componentId: "sc-1wb3jzc-0"
})(({
  theme,
  useOnePanel,
  matchPass,
  matchFail,
  isNestedGroup
}) => {
  let backgroundColor = undefined;
  if (useOnePanel) {
    if (isNestedGroup) {
      backgroundColor = 'transparent';
    } else if (matchPass) {
      backgroundColor = theme.color['fill-positive-subtle'];
    } else if (matchFail) {
      backgroundColor = theme.color['fill-alert-subtle'];
    } else {
      backgroundColor = theme.color['fill-surface-recessed'];
    }
  } else {
    backgroundColor = theme.color['fill-surface-default'];
  }
  return {
    padding: theme.space[300],
    borderRadius: useOnePanel ? theme.borderRadius[300] : theme.borderRadius[100],
    backgroundColor
  };
});
const StyledConditionBox = styled.div.attrs({
  'data-test-id': 'condition-box',
  'data-highlight-target': 'condition-box'
})(({
  useOnePanel,
  deleteHighlight,
  matchPass,
  matchFail,
  withError,
  isNestedGroup,
  theme
}) => {
  const getBaseBorderColor = () => {
    if (useOnePanel) {
      if (isNestedGroup) {
        return {
          borderColor: theme.color['border-core-default']
        };
      } else {
        return {
          borderColor: 'transparent'
        };
      }
    } else {
      return {
        borderColor: theme.color['border-core-hover']
      };
    }
  };
  const getOverrideBorderStyle = () => {
    if (deleteHighlight) {
      return {
        borderColor: theme.color['border-alert-default'],
        outline: useOnePanel ? undefined : `1px solid ${theme.color['border-alert-default']}`
      };
    } else if (withError) {
      return {
        borderColor: `${theme.color['border-alert-default']} !important`,
        outline: useOnePanel ? undefined : `1px solid ${theme.color['border-alert-default']}`
      };
    }
  };
  const getMatchBorderStyle = () => {
    if (matchPass) {
      return {
        borderColor: `${theme.color['border-positive-default']}`
      };
    } else if (matchFail) {
      return {
        borderColor: `${theme.color['border-alert-default']}`
      };
    }
  };
  return Object.assign({
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid',
    borderRadius: useOnePanel ? theme.borderRadius[300] : theme.borderRadius[100]
  }, getBaseBorderColor(), getMatchBorderStyle(), getOverrideBorderStyle());
});
const ConditionsList = styled.div.withConfig({
  displayName: "ViewerRelatedConditions__ConditionsList",
  componentId: "sc-1wb3jzc-1"
})(({
  theme
}) => ({
  '> *': {
    position: 'relative',
    marginTop: theme.space[50],
    marginLeft: theme.space[100]
  }
}));
const ViewerRelatedConditions = props => {
  const {
    logicGroup,
    matchStatusData,
    conditionPath,
    inputUse,
    isNestedGroup,
    objectName
  } = props;
  const {
    dispatch,
    isInEditMode,
    draftCondition,
    viewerConditionIdToHighlight,
    viewerConditionToDelete,
    viewerConditionToDeleteHovered,
    props: {
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    isTestFiltersEnabled
  } = useStrategy();
  const {
    onEditFilter
  } = useViewerActions();
  const logicGroupConditionsSeq = logicGroup.conditions.entrySeq();
  const isAddingNewCondition = draftCondition && draftCondition.isAddingNew && draftCondition.conditionPath.equals(conditionPath);
  const hasPropertyNameAndValueCondition = logicGroup.conditions.valueSeq().some(isPropertyNameAndValueCondition);
  const onConditionHighlightFinish = useCallback(() => {
    dispatch({
      type: 'viewerConditionIdToHighlight',
      payload: undefined
    });
  }, [dispatch]);
  const onSubFilterClick = useCallback((evt, path, filterFamily) => {
    evt.stopPropagation();
    onEditFilter(path, filterFamily);
  }, [onEditFilter]);
  const matchStatus = matchStatusData === null || matchStatusData === void 0 ? void 0 : matchStatusData.status;
  const testFiltersData = matchStatusData && 'filtersData' in matchStatusData ? matchStatusData.filtersData : undefined;
  const internalLogicOperator = logicGroup.logicGroupName;
  const getLogicSeparator = (hidden = false) => {
    return /*#__PURE__*/_jsx(Box, {
      flexGrow: 0,
      children: /*#__PURE__*/_jsx(Flex, {
        minInlineSize: "32px",
        justifyContent: "end",
        children: /*#__PURE__*/_jsx("div", {
          style: {
            visibility: hidden ? 'hidden' : 'visible'
          },
          children: internalLogicOperator === LogicGroupNames.AND ? I18n.text('newCustomerDataFilters.logicOperator.and') : I18n.text('newCustomerDataFilters.logicOperator.or')
        })
      })
    });
  };
  const matchPass = !!(useOnePanel && isTestFiltersEnabled && matchStatus === MatchStatus.PASS);
  const matchFail = !!(useOnePanel && isTestFiltersEnabled && matchStatus === MatchStatus.FAIL);
  return /*#__PURE__*/_jsx(StyledConditionBox, {
    useOnePanel: !!useOnePanel,
    deleteHighlight: !!(viewerConditionToDelete && conditionPath.equals(viewerConditionToDelete) || viewerConditionToDeleteHovered && conditionPath.equals(viewerConditionToDeleteHovered)),
    matchPass: matchPass,
    matchFail: matchFail,
    withError: !!logicGroup.errors.length,
    isNestedGroup: !!isNestedGroup,
    children: /*#__PURE__*/_jsxs(StyledConditionContentBox, {
      useOnePanel: !!useOnePanel,
      matchPass: matchPass,
      matchFail: matchFail,
      isNestedGroup: !!isNestedGroup,
      children: [/*#__PURE__*/_jsx(RefHighlightContainer, {
        onHighlightFinish: onConditionHighlightFinish,
        shouldHighlight: !!useOnePanel && getConditionId(conditionPath) === viewerConditionIdToHighlight,
        children: /*#__PURE__*/_jsx(ViewerRelatedConditionsHeading, {
          conditionPath: conditionPath,
          logicGroup: logicGroup,
          matchStatus: matchStatus,
          inputUse: inputUse
        })
      }), /*#__PURE__*/_jsxs(ConditionsList, {
        children: [logicGroupConditionsSeq.map(([key, condition], index) => {
          const currConditionPath = conditionPath.push(key);
          const testFilterData = testFiltersData && testFiltersData[index];
          if (isLogicGroup(condition)) {
            const conditionIds = condition.conditions.entrySeq().map(([conditionKey]) => getConditionId(currConditionPath.push(conditionKey))).concat(getConditionId(currConditionPath));
            const shouldHighlight = !useOnePanel && conditionIds.includes(viewerConditionIdToHighlight);
            return /*#__PURE__*/_jsxs(Flex, {
              gap: LOGIC_OPERATOR_GAP,
              alignItems: "baseline",
              children: [getLogicSeparator(index === 0 && !isAnyEventTypeId(logicGroup.filterFamily)), /*#__PURE__*/_jsx(Box, {
                flex: 1,
                minInlineSize: "0",
                children: /*#__PURE__*/_jsx(RefHighlightContainer, {
                  onHighlightFinish: onConditionHighlightFinish,
                  shouldHighlight: shouldHighlight,
                  targetSelector: ".condition-box",
                  children: /*#__PURE__*/_jsx(ViewerRelatedConditions, {
                    conditionPath: currConditionPath,
                    logicGroup: condition,
                    inputUse: inputUse,
                    isNestedGroup: true,
                    objectName: objectName
                  })
                })
              })]
            }, `${index}-${key}`);
          }
          return /*#__PURE__*/_jsxs(Flex, {
            gap: isInEditMode ? LOGIC_OPERATOR_GAP : '0',
            alignItems: "baseline",
            children: [getLogicSeparator(index === 0 && !isAnyEventTypeId(logicGroup.filterFamily)), /*#__PURE__*/_jsx(Box, {
              flex: 1,
              minInlineSize: "0",
              children: /*#__PURE__*/_jsxs(RefHighlightContainer, {
                onHighlightFinish: onConditionHighlightFinish,
                shouldHighlight: !!useOnePanel && getConditionId(currConditionPath) === viewerConditionIdToHighlight,
                children: [/*#__PURE__*/_jsx(ViewerConditionClickableBox, {
                  conditionPath: currConditionPath,
                  condition: condition,
                  filterFamily: logicGroup.filterFamily,
                  hasDeleteButton: true,
                  inputUse: inputUse,
                  onClick: evt => onSubFilterClick(evt, currConditionPath, logicGroup.filterFamily),
                  testFilterData: testFilterData,
                  use: "hover",
                  isInConditionContentBox: true,
                  isNestedOrHeader: true,
                  children: /*#__PURE__*/_jsx(ViewerCondition, {
                    condition: condition,
                    filterFamily: logicGroup.filterFamily,
                    parentLogicGroup: logicGroup,
                    testFilterData: testFilterData,
                    use: "plain",
                    isNested: true
                  })
                }), useOnePanel && testFilterData && /*#__PURE__*/_jsx(ViewerTestFiltersMembershipMatchDisplay, {
                  objectName: objectName,
                  matchStatus: testFilterData === null || testFilterData === void 0 ? void 0 : testFilterData.status
                })]
              })
            })]
          }, `${index}-${key}`);
        }), isAddingNewCondition && /*#__PURE__*/_jsxs(Flex, {
          gap: LOGIC_OPERATOR_GAP,
          alignItems: "baseline",
          children: [getLogicSeparator(), /*#__PURE__*/_jsx(Box, {
            flex: 1,
            children: /*#__PURE__*/_jsx(ViewerConditionPlaceholder, {})
          })]
        }), isInEditMode && !hasPropertyNameAndValueCondition && /*#__PURE__*/_jsxs(Flex, {
          gap: LOGIC_OPERATOR_GAP,
          alignItems: "baseline",
          paddingBlockStart: "100",
          children: [/*#__PURE__*/_jsx(Box, {
            children: getLogicSeparator()
          }), /*#__PURE__*/_jsx(ViewerAddFilterButton, {
            parentGroupKeyPath: conditionPath,
            filterFamily: logicGroup.filterFamily
          })]
        })]
      }), logicGroup.errors.map((error, index) => /*#__PURE__*/_jsx(Text, {
        display: "block",
        marginBlockStart: "200",
        typography: "heading-25",
        use: "error",
        children: error.message
      }, `${conditionPath.join('_')}_error_${index}`))]
    })
  });
};
export default ViewerRelatedConditions;